<script lang="ts">
  import { tick } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import { api } from '../js/api.js';
  import { auth } from '../js/auth.js';
  import { toast } from '../js/toast.js';
  import { spinner } from '../js/spinner.js';
  import { loadStripeScript } from '../js/stripe-loader.js';
  import Modal from './Modal.svelte';

  export let showModal = false;

  const dispatch = createEventDispatcher();

  let plans: any[] = [];
  let packs: any[] = [];
  let teamData: any = null;
  let isLoading = true;
  let modalWidth = 720;

  // Tabs
  let activeTab: 'plans' | 'packs' = 'plans';

  // Steps: choose -> checkout (for both plan and pack)
  type Step = 'choose' | 'checkout-pack' | 'checkout-plan';
  let step: Step = 'choose';
  let selectedPack: any = null;
  let selectedPlan: any = null;

  // Stripe for inline checkout
  let stripe: any;
  let elements: any;
  let cardNumber: any;
  let cardExpiry: any;
  let cardCvc: any;
  let cardholderName = '';
  let isPaying = false;
  let savedCards: { id: string; brand: string; last4: string; is_default?: boolean }[] = [];
  let selectedCardId: string | null = null;
  let useNewCard = false;
  let isLoadingCards = false;

  const stripePublishableKey = typeof import.meta.env.VITE_STRIPE_KEY === 'string' ? (import.meta.env.VITE_STRIPE_KEY || '').trim() : '';

  $: if (showModal) {
    step = 'choose';
    selectedPack = null;
    selectedPlan = null;
    loadData();
  }

  $: if (!showModal) {
    step = 'choose';
    selectedPack = null;
    selectedPlan = null;
    resetPaymentState();
  }

  function resetPaymentState() {
    savedCards = [];
    selectedCardId = null;
    useNewCard = false;
    cardholderName = '';
    destroyCardElements();
  }

  async function loadData() {
    isLoading = true;
    try {
      const teamUuid = ($auth as any).user?.active_team_uuid;
      const promises: Promise<any>[] = [
        api.getPaymentPlans() as Promise<any>,
        api.getPaymentPlanPacks() as Promise<any>,
      ];
      if (teamUuid) {
        promises.push(api.getTeam(teamUuid) as Promise<any>);
      }

      const [plansResponse, packsResponse, teamResponse] = await Promise.all(promises);

      if (plansResponse.success) {
        plans = plansResponse.data.plans || [];
      }

      if (packsResponse.success) {
        packs = packsResponse.data.packs ?? [];
      }

      if (teamResponse && teamResponse.success) {
        teamData = teamResponse.data?.team || null;
      }

      if (getUpgradePlans().length === 0) {
        activeTab = 'packs';
      } else {
        activeTab = 'plans';
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      isLoading = false;
    }
  }

  function getCurrentPlanName(): string {
    return teamData?.payment_plan?.name ?? 'No plan';
  }

  function getCurrentPlanPrice(): number {
    return teamData?.payment_plan?.price ?? 0;
  }

  function getCurrentPlanBriefs(): number {
    return teamData?.payment_plan?.included_briefs ?? 0;
  }

  function getUpgradePlans(): any[] {
    const currentPrice = getCurrentPlanPrice();
    return plans.filter(p => p.price > currentPrice && p.is_active);
  }

  async function initStripeAndCards() {
    if (stripePublishableKey && !stripe) {
      const StripeFn = await loadStripeScript();
      if (StripeFn) {
        stripe = StripeFn(stripePublishableKey);
        elements = stripe.elements();
      }
    }
    await loadSavedCards();
  }

  async function selectPlan(plan: any) {
    selectedPlan = plan;
    selectedPack = null;
    step = 'checkout-plan';
    await initStripeAndCards();
  }

  async function selectPack(pack: any) {
    selectedPack = pack;
    selectedPlan = null;
    step = 'checkout-pack';
    await initStripeAndCards();
  }

  function goBackToChoose() {
    step = 'choose';
    selectedPack = null;
    selectedPlan = null;
    resetPaymentState();
  }

  async function loadSavedCards() {
    if (!($auth as any).user?.uuid) return;
    isLoadingCards = true;
    try {
      const response = (await api.getUserCards(($auth as any).user.uuid)) as any;
      if (response?.success && response.data) {
        savedCards = response.data;
        if (savedCards.length > 0) {
          const defaultCard = response.data.find((c: any) => c.is_default) || response.data[0];
          selectedCardId = defaultCard.id;
          useNewCard = false;
        } else {
          useNewCard = true;
          tick().then(() => setupCardElements());
        }
      } else {
        savedCards = [];
        useNewCard = true;
        tick().then(() => setupCardElements());
      }
    } catch (error) {
      console.error('Error loading cards:', error);
      savedCards = [];
      useNewCard = true;
      tick().then(() => setupCardElements());
    } finally {
      isLoadingCards = false;
    }
  }

  function handleUseNewCard() {
    useNewCard = true;
    selectedCardId = null;
    tick().then(() => setupCardElements());
  }

  function destroyCardElements() {
    if (cardNumber) { cardNumber.destroy(); cardNumber = null; }
    if (cardExpiry) { cardExpiry.destroy(); cardExpiry = null; }
    if (cardCvc) { cardCvc.destroy(); cardCvc = null; }
  }

  function setupCardElements() {
    destroyCardElements();
    const numberEl = document.getElementById('upgrade-card-number');
    const expiryEl = document.getElementById('upgrade-card-expiry');
    const cvcEl = document.getElementById('upgrade-card-cvc');
    if (!stripe || !elements || !numberEl || !expiryEl || !cvcEl) return;

    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Poppins", "Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': { color: '#aab7c4' },
      },
      invalid: { color: '#fa755a', iconColor: '#fa755a' },
    };
    cardNumber = elements.create('cardNumber', { style });
    cardNumber.mount('#upgrade-card-number');
    cardExpiry = elements.create('cardExpiry', { style });
    cardExpiry.mount('#upgrade-card-expiry');
    cardCvc = elements.create('cardCvc', { style });
    cardCvc.mount('#upgrade-card-cvc');
  }

  function getCardBrandIcon(brand: string): string {
    const map: Record<string, string> = {
      visa: 'cc visa', mastercard: 'cc mastercard', amex: 'cc amex',
      discover: 'cc discover', diners: 'cc diners club', jcb: 'cc jcb',
    };
    return map[brand?.toLowerCase()] || 'credit card';
  }

  function capitalizeFirst(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  async function getPaymentMethodId(): Promise<string | null> {
    if (useNewCard) {
      if (!stripe) {
        toast.showNotification(false, 'Payment is not configured.');
        return null;
      }
      if (!cardholderName?.trim()) {
        toast.showNotification(false, 'Please enter the name on card');
        return null;
      }
      try {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumber,
          billing_details: { name: cardholderName },
        });
        if (error) {
          toast.showNotification(false, error.message);
          return null;
        }
        return paymentMethod.id;
      } catch (err) {
        toast.showNotification(false, 'Error processing card');
        return null;
      }
    } else {
      if (!selectedCardId) {
        toast.showNotification(false, 'Please select a payment method');
        return null;
      }
      return selectedCardId;
    }
  }

  async function handlePackPayment() {
    if (!selectedPack) return;
    isPaying = true;

    const paymentMethodId = await getPaymentMethodId();
    if (!paymentMethodId) { isPaying = false; return; }

    spinner.show();
    try {
      const teamUuid = ($auth as any).user?.active_team_uuid;
      if (!teamUuid) {
        toast.showNotification(false, 'No active team found.');
        isPaying = false;
        spinner.hide();
        return;
      }
      const response = await api.purchaseTeamPack(
        teamUuid,
        selectedPack.uuid,
        paymentMethodId
      ) as { success?: boolean; message?: string };
      if (response.success) {
        showModal = false;
        toast.showNotification(true, 'Pack purchased! Generating your brief...');
        dispatch('upgraded');
      } else {
        toast.showNotification(false, (response as any).message || 'Failed to purchase pack');
      }
    } catch (error) {
      toast.showNotification(false, 'Error purchasing pack');
      console.error(error);
    } finally {
      spinner.hide();
      isPaying = false;
    }
  }

  async function handlePlanPayment() {
    if (!selectedPlan) return;
    isPaying = true;

    const paymentMethodId = await getPaymentMethodId();
    if (!paymentMethodId) { isPaying = false; return; }

    spinner.show();
    try {
      const teamUuid = ($auth as any).user?.active_team_uuid;
      if (!teamUuid) {
        toast.showNotification(false, 'No active team found.');
        isPaying = false;
        spinner.hide();
        return;
      }
      const response = await api.assignTeamPlan(
        teamUuid,
        selectedPlan.uuid,
        'monthly',
        paymentMethodId
      ) as any;
      if (response.success) {
        showModal = false;
        toast.showNotification(true, `Successfully subscribed to ${selectedPlan.name}!`);
        dispatch('upgraded');
      } else {
        toast.showNotification(false, response.message || 'Failed to subscribe to plan');
      }
    } catch (error) {
      toast.showNotification(false, 'Error subscribing to plan');
      console.error(error);
    } finally {
      spinner.hide();
      isPaying = false;
    }
  }

  function handleCheckoutSubmit() {
    if (step === 'checkout-pack') {
      handlePackPayment();
    } else if (step === 'checkout-plan') {
      handlePlanPayment();
    }
  }

  function formatPrice(price: number): string {
    return new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }

  function getCheckoutLabel(): string {
    if (step === 'checkout-pack' && selectedPack) {
      return `${selectedPack.brief_count} Briefs Pack`;
    }
    if (step === 'checkout-plan' && selectedPlan) {
      return selectedPlan.name;
    }
    return '';
  }

  function getCheckoutAmount(): number {
    if (step === 'checkout-pack' && selectedPack) return selectedPack.price;
    if (step === 'checkout-plan' && selectedPlan) return selectedPlan.price;
    return 0;
  }

  function getHeaderTitle(): string {
    if (step === 'checkout-pack') return 'Purchase Brief Pack';
    if (step === 'checkout-plan') return 'Subscribe to Plan';
    return "You've used all your briefs";
  }

  function getPayButtonLabel(): string {
    const amount = formatPrice(getCheckoutAmount());
    if (step === 'checkout-plan') return `Subscribe ${amount}/mo`;
    return `Pay ${amount}`;
  }
</script>

<Modal bind:showModal bind:modalWidth modalClass="upgrade-prompt-modal">
  <div slot="header">{getHeaderTitle()}</div>
  <div slot="body">
    {#if isLoading}
      <div class="loading-state">
        <i class="spinner loading icon"></i>
        Loading options...
      </div>

    {:else if step === 'choose'}
      <!-- Current plan banner -->
      <div class="current-plan-banner">
        <div class="current-plan-icon">
          <i class="clipboard outline icon"></i>
        </div>
        <div class="current-plan-info">
          <span class="current-plan-label">Current plan</span>
          <span class="current-plan-name">{getCurrentPlanName()}</span>
        </div>
        <div class="current-plan-detail">
          {#if getCurrentPlanPrice() > 0}
            {formatPrice(getCurrentPlanPrice())}/mo
          {:else}
            Free
          {/if}
          &middot; {getCurrentPlanBriefs()} brief{getCurrentPlanBriefs() !== 1 ? 's' : ''}/month
        </div>
      </div>

      <p class="prompt-description">Upgrade your plan or purchase a brief pack to continue.</p>

      <!-- Tabs -->
      <div class="tabs">
        {#if getUpgradePlans().length > 0}
          <button
            class="tab"
            class:active={activeTab === 'plans'}
            on:click={() => activeTab = 'plans'}
          >
            Upgrade Plan
          </button>
        {/if}
        <button
          class="tab"
          class:active={activeTab === 'packs'}
          on:click={() => activeTab = 'packs'}
        >
          Buy a Brief Pack
        </button>
      </div>

      <!-- Tab content -->
      <div class="tab-content">
        {#if activeTab === 'plans' && getUpgradePlans().length > 0}
          <div class="plans-list">
            {#each getUpgradePlans() as plan (plan.uuid)}
              <div class="plan-option">
                <div class="plan-details">
                  <div class="plan-name">{plan.name}</div>
                  <div class="plan-meta">
                    <span class="plan-price">{formatPrice(plan.price)}<span class="price-period">/mo</span></span>
                    <span class="plan-briefs">{plan.included_briefs} briefs/month</span>
                  </div>
                </div>
                <button class="upgrade-button" on:click={() => selectPlan(plan)}>
                  Upgrade
                </button>
              </div>
            {/each}
          </div>

        {:else if activeTab === 'packs'}
          <div class="packs-grid">
            {#each packs as pack (pack.uuid)}
              <div class="pack-card">
                <div class="pack-briefs-count">{pack.brief_count}</div>
                <div class="pack-briefs-label">briefs</div>
                <div class="pack-price">{formatPrice(pack.price)}</div>
                <button class="pack-buy-button" on:click={() => selectPack(pack)}>
                  Purchase
                </button>
              </div>
            {/each}
          </div>
          <p class="packs-note">One-time purchase. Use across billing periods.</p>
        {/if}
      </div>

    {:else}
      <!-- Checkout step (shared for both plan and pack) -->

      <!-- Order summary -->
      <div class="order-summary">
        <div class="order-summary-item">
          <span class="order-summary-label">{getCheckoutLabel()}</span>
          <span class="order-summary-amount">
            {formatPrice(getCheckoutAmount())}{#if step === 'checkout-plan'}<span class="amount-period">/mo</span>{/if}
          </span>
        </div>
        {#if step === 'checkout-plan' && selectedPlan}
          <div class="order-summary-features">
            <span class="order-feature"><i class="check circle icon"></i> {selectedPlan.included_briefs} briefs/month</span>
            {#if selectedPlan.extra_brief_price}
              <span class="order-feature"><i class="plus circle icon"></i> Extra briefs at {formatPrice(selectedPlan.extra_brief_price)} each</span>
            {/if}
          </div>
          <div class="billing-note">
            <i class="calendar alternate outline icon"></i>
            <span>You'll be charged {formatPrice(selectedPlan.price)} monthly</span>
          </div>
        {/if}
      </div>

      <!-- Payment method -->
      <div class="payment-section">
        <h4 class="section-title">Payment Method</h4>

        {#if isLoadingCards}
          <div class="cards-loading">
            <i class="spinner loading icon"></i>
            <span>Loading payment methods...</span>
          </div>
        {:else}
          {#if savedCards.length > 0}
            <div class="payment-options">
              {#each savedCards as card}
                <label class="payment-option" class:selected={!useNewCard && selectedCardId === card.id}>
                  <input
                    type="radio"
                    name="upgrade-payment-method"
                    value={card.id}
                    checked={!useNewCard && selectedCardId === card.id}
                    on:change={() => { selectedCardId = card.id; useNewCard = false; }}
                  />
                  <div class="option-content">
                    <i class="{getCardBrandIcon(card.brand)} icon card-brand-icon"></i>
                    <span class="card-info">{capitalizeFirst(card.brand)} &bull;&bull;&bull;&bull; {card.last4}</span>
                  </div>
                </label>
              {/each}
              <label class="payment-option" class:selected={useNewCard}>
                <input
                  type="radio"
                  name="upgrade-payment-method"
                  value="new"
                  checked={useNewCard}
                  on:change={handleUseNewCard}
                />
                <div class="option-content">
                  <i class="plus icon"></i>
                  <span>Add new card</span>
                </div>
              </label>
            </div>
          {/if}

          {#if useNewCard}
            {#if !stripePublishableKey}
              <p class="stripe-not-configured">Payment is not configured. Please set VITE_STRIPE_KEY in your environment.</p>
            {:else}
              <div class="new-card-form" class:has-saved-cards={savedCards.length > 0}>
                <div class="form-field">
                  <label for="upgrade-cardholder-name" class="field-label">Name on Card</label>
                  <input
                    id="upgrade-cardholder-name"
                    class="field-input"
                    type="text"
                    bind:value={cardholderName}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div class="form-field">
                  <span class="field-label" id="upgrade-card-number-label">Card Number</span>
                  <div id="upgrade-card-number" class="stripe-input" aria-labelledby="upgrade-card-number-label"></div>
                </div>
                <div class="form-row">
                  <div class="form-field half">
                    <span class="field-label" id="upgrade-card-expiry-label">Expiry Date</span>
                    <div id="upgrade-card-expiry" class="stripe-input" aria-labelledby="upgrade-card-expiry-label"></div>
                  </div>
                  <div class="form-field half">
                    <span class="field-label" id="upgrade-card-cvc-label">CVC</span>
                    <div id="upgrade-card-cvc" class="stripe-input cvc" aria-labelledby="upgrade-card-cvc-label"></div>
                  </div>
                </div>
              </div>
            {/if}
          {/if}
        {/if}
      </div>
    {/if}
  </div>
  <div slot="footer">
    {#if step !== 'choose'}
      <div class="footer-actions">
        <button class="back-button" on:click={goBackToChoose} disabled={isPaying}>
          <i class="arrow left icon"></i> Back
        </button>
        <button
          class="pay-button"
          on:click={handleCheckoutSubmit}
          disabled={isPaying || isLoadingCards || (useNewCard && (!stripePublishableKey || !cardholderName?.trim())) || (!useNewCard && !selectedCardId)}
        >
          {#if isPaying}
            <i class="spinner loading icon"></i> Processing...
          {:else}
            {getPayButtonLabel()}
          {/if}
        </button>
      </div>
    {:else}
      <button class="close-button" on:click={() => showModal = false}>Cancel</button>
    {/if}
  </div>
</Modal>

<style>
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2rem;
    color: var(--grey-secondary);
  }

  /* Current plan banner */
  .current-plan-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, #f0f4f8 0%, #e8edf3 100%);
    border-radius: 10px;
    margin-bottom: 1rem;
  }

  .current-plan-icon {
    width: 36px;
    height: 36px;
    background: var(--primary);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .current-plan-icon i {
    color: var(--white);
    font-size: 1.1rem;
    margin: 0;
  }

  .current-plan-info {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .current-plan-label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--grey-secondary);
    font-weight: 500;
  }

  .current-plan-name {
    font-weight: 600;
    color: var(--grey);
    font-size: 0.9375rem;
  }

  .current-plan-detail {
    margin-left: auto;
    font-size: 0.8125rem;
    color: var(--grey-secondary);
    white-space: nowrap;
  }

  .prompt-description {
    color: var(--grey-secondary);
    margin: 0 0 1rem 0;
    line-height: 1.6;
    font-size: 0.9375rem;
  }

  /* Tabs */
  .tabs {
    display: flex;
    gap: 0;
    border-bottom: 2px solid var(--border-color);
    margin-bottom: 1.25rem;
  }

  .tab {
    padding: 0.625rem 1.25rem;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--grey-secondary);
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s;
  }

  .tab:hover {
    color: var(--grey);
  }

  .tab.active {
    color: var(--primary);
    border-bottom-color: var(--primary);
  }

  /* Plans list */
  .plans-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .plan-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .plan-option:hover {
    border-color: var(--primary);
    box-shadow: 0 2px 8px rgba(5, 108, 242, 0.1);
  }

  .plan-details {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .plan-name {
    font-weight: 600;
    color: var(--grey);
    font-size: 1rem;
  }

  .plan-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 0.875rem;
  }

  .plan-price {
    font-weight: 600;
    color: var(--primary);
  }

  .price-period {
    font-weight: 400;
    color: var(--grey-secondary);
    font-size: 0.8125rem;
  }

  .plan-briefs {
    color: var(--grey-secondary);
  }

  .upgrade-button {
    padding: 0.5rem 1.25rem;
    background: var(--primary);
    color: var(--white);
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
    transition: background-color 0.2s;
    white-space: nowrap;
  }

  .upgrade-button:hover {
    background: var(--darkprimary);
  }

  /* Packs grid */
  .packs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1rem;
  }

  .pack-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.25rem 1rem;
    background: var(--white);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    text-align: center;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .pack-card:hover {
    border-color: var(--primary);
    box-shadow: 0 2px 8px rgba(5, 108, 242, 0.1);
  }

  .pack-briefs-count {
    font-size: 2rem;
    font-weight: 700;
    color: var(--primary);
    line-height: 1;
  }

  .pack-briefs-label {
    font-size: 0.8125rem;
    color: var(--grey-secondary);
    margin-bottom: 0.5rem;
  }

  .pack-price {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--grey);
    margin-bottom: 0.75rem;
  }

  .pack-buy-button {
    width: 100%;
    padding: 0.5rem 1rem;
    background: var(--primary);
    color: var(--white);
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
    transition: background-color 0.2s;
  }

  .pack-buy-button:hover {
    background: var(--darkprimary);
  }

  .packs-note {
    margin: 0.75rem 0 0 0;
    font-size: 0.8125rem;
    color: var(--grey-secondary);
    text-align: center;
  }

  /* Checkout step - order summary */
  .order-summary {
    background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
    border-radius: 10px;
    padding: 1rem 1.25rem;
    margin-bottom: 1.25rem;
  }

  .order-summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .order-summary-label {
    font-weight: 600;
    color: var(--grey);
    font-size: 1.0625rem;
  }

  .order-summary-amount {
    font-weight: 700;
    color: var(--primary);
    font-size: 1.25rem;
  }

  .amount-period {
    font-weight: 400;
    color: var(--grey-secondary);
    font-size: 0.875rem;
  }

  .order-summary-features {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .order-feature {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8125rem;
    color: var(--grey);
  }

  .order-feature i {
    color: #45c496;
    font-size: 0.875rem;
    margin: 0;
  }

  .billing-note {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.625rem 0.75rem;
    background: var(--white);
    border-radius: 6px;
    font-size: 0.8125rem;
    color: var(--grey-secondary);
  }

  .billing-note i {
    color: var(--primary);
    margin: 0;
    font-size: 0.875rem;
  }

  /* Payment section */
  .payment-section {
    margin-top: 0;
  }

  .section-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--grey);
    margin: 0 0 1rem 0;
  }

  .cards-loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    color: var(--grey-secondary);
    font-size: 0.875rem;
  }

  .payment-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .payment-option {
    display: flex;
    align-items: center;
    padding: 0.875rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
  }

  .payment-option:hover {
    border-color: var(--primary);
    background: #f8fafc;
  }

  .payment-option.selected {
    border-color: var(--primary);
    background: rgba(5, 108, 242, 0.05);
  }

  .payment-option input[type="radio"] {
    margin-right: 0.75rem;
    accent-color: var(--primary);
  }

  .option-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  .card-brand-icon {
    font-size: 1.5rem !important;
    color: var(--grey);
    margin: 0 !important;
  }

  .card-info {
    flex: 1;
    font-size: 0.9375rem;
    color: var(--grey);
  }

  .stripe-not-configured {
    padding: 1rem;
    background: #fef3c7;
    color: #92400e;
    border-radius: 8px;
    font-size: 0.9375rem;
    margin: 0;
  }

  .new-card-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .new-card-form.has-saved-cards {
    padding-top: 0.5rem;
    border-top: 1px solid var(--border-color);
    margin-top: 0.5rem;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-field.half {
    flex: 1;
  }

  .field-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--grey);
  }

  .field-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 1rem;
    font-family: inherit;
    background: rgb(var(--input-bg));
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .field-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .stripe-input {
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--white);
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .stripe-input.cvc {
    max-width: 120px;
  }

  .stripe-input:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  /* Footer */
  .footer-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .close-button {
    padding: 0.5rem 1.25rem;
    background: var(--white);
    color: var(--grey);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
  }

  .close-button:hover {
    background: var(--greyish);
    border-color: var(--grey-secondary);
  }

  .back-button {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 1.25rem;
    background: var(--white);
    color: var(--grey);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
  }

  .back-button i {
    margin: 0;
    font-size: 0.875rem;
  }

  .back-button:hover:not(:disabled) {
    background: var(--greyish);
    border-color: var(--grey-secondary);
  }

  .back-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .pay-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1.5rem;
    background: var(--primary);
    color: var(--white);
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.9375rem;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
    transition: background-color 0.2s;
  }

  .pay-button:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .pay-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .pay-button i {
    margin: 0;
  }

  @media (max-width: 480px) {
    .current-plan-banner {
      flex-wrap: wrap;
    }
    .current-plan-detail {
      margin-left: 0;
      width: 100%;
      padding-left: calc(36px + 0.75rem);
    }
    .packs-grid {
      grid-template-columns: 1fr 1fr;
    }
    .form-row {
      grid-template-columns: 1fr;
    }
  }
</style>
